import { useAPI } from '@providers/useAPI';
import { useQueryParams } from '@providers/useQueryParams';
import { useMutation } from '@tanstack/react-query';
import type { PasswdBodyDto } from 'app/API';
import { createContext, useContext, useRef, useState, ReactNode } from 'react';
import { resetPassword } from '../api/resetPassword';
import { getPasswordResetError, PASSWORD_RESET_FALLBACK_ERROR } from '../helpers/getPasswordResetError';

const EXPIRED_CODE_ERROR = 'ExpiredCodeException';

type TState = {
  isComplete: boolean;
  isExpired: boolean;
  isLoading: boolean;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  confirmation: string;
  setConfirmation: (v: string) => void;
  code: string;
  setCode: (v: string) => void;
  error: string;
  setError: (v: string) => void;
  reset: () => void;
};

const PageContext = createContext<TState>({
  isComplete: false,
  isExpired: false,
  isLoading: false,
  email: '',
  setEmail: () => null,
  password: '',
  setPassword: () => null,
  confirmation: '',
  setConfirmation: () => null,
  code: '',
  setCode: () => null,
  error: '',
  setError: () => null,
  reset: () => null,
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryParams = useQueryParams();
  const [email, setEmail] = useState(() => queryParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState(() => queryParams.get('code') ?? '');
  const [error, setError] = useState('');
  const isResetInFlight = useRef(false);

  const resetMutation = useMutation({
    mutationFn: (requestBody: PasswdBodyDto) => resetPassword({ call, requestBody }),
    onSuccess: (data) => {
      setError(getPasswordResetError(data));
    },
    onError: () => {
      setError(PASSWORD_RESET_FALLBACK_ERROR);
    },
    onSettled: () => {
      isResetInFlight.current = false;
    },
  });

  const responseError = resetMutation.isSuccess ? getPasswordResetError(resetMutation.data) : '';

  const reset = () => {
    if (isResetInFlight.current) {
      return;
    }
    isResetInFlight.current = true;
    resetMutation.mutate({ password, email, code });
  };

  return (
    <PageContext.Provider
      value={{
        isComplete: resetMutation.isSuccess && !responseError,
        isExpired: responseError === EXPIRED_CODE_ERROR,
        isLoading: resetMutation.isPending,
        email,
        setEmail,
        password,
        setPassword,
        confirmation,
        setConfirmation,
        code,
        setCode,
        error,
        setError,
        reset,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
